// ChangeCardsProject.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSocketConnectionId } from '@contexts/SocketContext';
import ComboBoxProject from '@components/Globals/ComboBoxProject';
// import ProjectService from '../../services/Project/ProjectService';
import { dashboardService } from '@services/dashboardService';
import './ChangeCardsProject.css';

// ==================== TYPES ====================

interface ProjectRequest {
  ConfigValueId: number;
  Filters: any[];
  IsFromProject: boolean;
  ProjectName: string;
  GroupByLayerId: number;
  GroupByDetailId: number;
  GroupByValueField: string;
  ProjectId?: number;
  IsPreview?: number;
  configValueName: string;
  ConversationsIds?: string;
  SocketConnectionId?: string;
  DuplicateConversationDetails?: ConversationDetail[];
  DashboardId?: number;
}

interface ProjectOption {
  label: string;
  value: number | string;
}

interface ConversationDetail {
  ConversationId: number | string;
  DashboardGroupId: number;
  DashboardGroupConversationId: number;
  IsParent: boolean;
  IsArchived: boolean;
  Progress: number;
}

interface DashboardGroup {
  IsDefault: boolean;
  DashboardId: number;
  Conversations: ConversationDetail[];
}

interface ConversationSummary {
  ConversationId: number | string;
  Status: boolean;
  Title: string;
  ConfigValueIcon: string;
  PrevProjectName?: string;
  NewProjectName?: string;
  ErrorMessage?: string;
}

interface ChangeCardsProjectStep1Props {
  request: ProjectRequest;
  onSelectProjectChange: (projectId: number) => void;
  onPreviewSelected: () => void;
  onCancelSelected: (goToDuplicateMenu?: boolean, closeModal?: boolean) => void;
  dashboardContext: DashboardGroup[] | Record<string, never>;
}

interface ChangeCardsProjectStep2Props {
  request: ProjectRequest;
  onConversationsUpdated?: (items: ConversationSummary[]) => void;
  onCancelSummarySelected: () => void;
  dashboardContext: DashboardGroup[] | Record<string, never>;
}

interface ChangeCardsProjectProps {
  request: ProjectRequest;
  onConversationsUpdated?: (items: ConversationSummary[]) => void;
  onClose?: (goToDuplicateMenu?: boolean, closeModal?: boolean) => void;
  dashboardContext?: DashboardGroup[] | Record<string, never>;
}

// ==================== HELPERS ====================

const isDashboardContextEmpty = (ctx: DashboardGroup[] | Record<string, never>): boolean =>
  JSON.stringify(ctx) === '{}';

// ==================== STEP 1 ====================

const ChangeCardsProjectStep1: React.FC<ChangeCardsProjectStep1Props> = ({
  request,
  onSelectProjectChange,
  onPreviewSelected,
  onCancelSelected,
  dashboardContext,
}) =>
{
  const [selectedValue, setSelectedValue] = useState<ProjectOption>({
    label: '',
    value: '',
  });

  useEffect(() =>
  {
    onSelectProjectChange(0);
  }, [onSelectProjectChange]);

  const handleSelectProjectChange = useCallback(
    (itemSelected: ProjectOption | null) =>
    {
      let projectIdUpd: number = 0;

      if (itemSelected !== null)
      {
        projectIdUpd =
          typeof itemSelected.value === 'number'
            ? itemSelected.value
            : parseInt(itemSelected.value as string, 10);

        setSelectedValue({ value: projectIdUpd, label: itemSelected.label });
      }
      else
      {
        projectIdUpd = 0;
        setSelectedValue({ value: '', label: '' });
      }

      onSelectProjectChange(projectIdUpd);
    },
    [onSelectProjectChange],
  );

  const handleCancelSelected = (goToDuplicateMenu?: boolean, closeModal?: boolean) =>
  {
    if (isDashboardContextEmpty(dashboardContext))
    {
      onCancelSelected();
    }
    else
    {
      onCancelSelected(goToDuplicateMenu, closeModal);
    }
  };

  // onBlur: scroll cleanup logic removed since it relied fully on jQuery
  // and external AngularJS container selectors. Material-UI/React handles
  // scroll/overflow natively. Add scroll listeners here if still needed.
  const handleBlur = useCallback(() => {}, []);

  const requestVM: ProjectRequest = {
    ConfigValueId: 9,
    Filters: [],
    IsFromProject: false,
    ProjectName: '',
    GroupByLayerId: 0,
    GroupByDetailId: 0,
    GroupByValueField: '',
    configValueName: request.configValueName,
  };

  const isDark = document.documentElement.classList.contains('dark');
  const configValueName = request.configValueName;

  return (
    <div>
      <div style={{ padding: '24px' }}>
        <div className="update-item">
          <div className="update-items-header">
            <div style={{ fontSize: '10px', color: isDark ? '#6e6860' : '#a5a7ac' }}>
              {configValueName}:
            </div>
            <div className="update-items-project" style={{ textAlign: 'left' }}>
              <ComboBoxProject
                name="form-field-name"
                style={{ textAlign: 'left', width: '250px' }}
                onDataSelected={handleSelectProjectChange as any}
                onBlur={handleBlur}
                requestVM={requestVM}
                openOnFocus={true}
                autoFocus={true}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="react-corgee-wizard_modal-footer flex justify-end gap-3 mt-5">
        <button
          className="react-corgee-wizard_cancel-btn btn btn-default"
          onClick={() => handleCancelSelected(true, false)}
        >
          Cancel
        </button>
        <button
          disabled={selectedValue.value === ''}
          className="react-corgee-wizard_corgee-btn btn btn-default"
          onClick={onPreviewSelected}
        >
          Preview
        </button>
      </div>
    </div>
  );
};

// ==================== STEP 2 ====================

const ChangeCardsProjectStep2: React.FC<ChangeCardsProjectStep2Props> = ({
  request,
  onConversationsUpdated,
  onCancelSummarySelected,
  dashboardContext,
}) =>
{
  const socketConnectionId = useSocketConnectionId();
  const [summary, setSummary] = useState<ConversationSummary[]>([]);
  const [canChange, setCanChange] = useState(false);
  const [validConversations, setValidConversations] = useState<(number | string)[]>([]);

  // Mirrors the original mapConversations method
  const mapConversations = useCallback(
    (dashboardContextObj: DashboardGroup[]): ConversationDetail[] =>
    {
      return dashboardContextObj
        .filter(group => !group.IsDefault)
        .flatMap(group => group.Conversations)
        .map(
          ({
            ConversationId,
            DashboardGroupId,
            DashboardGroupConversationId,
            IsParent,
            IsArchived,
            Progress,
          }) => ({
            ConversationId,
            DashboardGroupId,
            DashboardGroupConversationId,
            IsParent,
            IsArchived,
            Progress,
          }),
        );
    },
    [],
  );

  // Load preview on mount — mirrors componentDidMount logic exactly
  useEffect(() =>
  {
    const loadPreview = async () =>
    {
      const previewRequest: ProjectRequest = {
        ConversationsIds: request.ConversationsIds,
        configValueName: request.configValueName,
        SocketConnectionId: socketConnectionId,
        IsPreview: 1,
        ProjectId: request.ProjectId,
        ConfigValueId: request.ConfigValueId ?? 0,
        Filters: request.Filters ?? [],
        IsFromProject: request.IsFromProject ?? false,
        ProjectName: request.ProjectName ?? '',
        GroupByLayerId: request.GroupByLayerId ?? 0,
        GroupByDetailId: request.GroupByDetailId ?? 0,
        GroupByValueField: request.GroupByValueField ?? '',
      };

      try
      {
        let response: ConversationSummary[];

        if (isDashboardContextEmpty(dashboardContext))
        {
          response = (await dashboardService.ChangeConversationsProject(
            previewRequest as any,
          )) as any;
        }
        else
        {
          const ctx = dashboardContext as DashboardGroup[];
          const duplicateConversationDetails = mapConversations(ctx);

          previewRequest.DuplicateConversationDetails = duplicateConversationDetails;
          previewRequest.DashboardId = ctx[0]?.DashboardId ?? 0;

          response = (await dashboardService.DuplicateDashboardProject(
            previewRequest as any,
          )) as any;
        }

        if (response)
        {
          const successIds = response
            .filter(item => item.Status === true)
            .map(item => item.ConversationId);

          setSummary(response);
          setCanChange(successIds.length > 0);
          // Mirrors: this.handleValidConversations(response.filter(c => c.Status).map(...))
          setValidConversations(response.filter(c => c.Status).map(c => c.ConversationId));
        }
        else
        {
          setSummary([]);
          setCanChange(false);
        }
      }
      catch (error)
      {
        console.error('Error loading preview:', error);
        setSummary([]);
        setCanChange(false);
      }
    };

    loadPreview();
  }, [request, dashboardContext, mapConversations]);

  // Mirrors onConversationsUpdated
  const handleConversationsUpdated = useCallback(
    (itemsUpdated: ConversationSummary[]) =>
    {
      if (onConversationsUpdated)
      {
        const data = itemsUpdated ? itemsUpdated.filter(item => (item.Status as any) === 1) : [];
        onConversationsUpdated(data);
      }
    },
    [onConversationsUpdated],
  );

  // Mirrors onDashboardDuplicated
  const handleDashboardDuplicated = useCallback(
    (response: ConversationSummary[]) =>
    {
      if (onConversationsUpdated && response)
      {
        onConversationsUpdated(response);
      }
    },
    [onConversationsUpdated],
  );

  // Mirrors onApplySelected exactly
  const handleApplySelected = useCallback(async () =>
  {
    const applyRequest: ProjectRequest = {
      ConversationsIds: request.ConversationsIds,
      configValueName: request.configValueName,
      SocketConnectionId: socketConnectionId,
      IsPreview: 0,
      ProjectId: request.ProjectId,
      ConfigValueId: request.ConfigValueId ?? 0,
      Filters: request.Filters ?? [],
      IsFromProject: request.IsFromProject ?? false,
      ProjectName: request.ProjectName ?? '',
      GroupByLayerId: request.GroupByLayerId ?? 0,
      GroupByDetailId: request.GroupByDetailId ?? 0,
      GroupByValueField: request.GroupByValueField ?? '',
    };

    try
    {
      if (isDashboardContextEmpty(dashboardContext))
      {
        const response = (await dashboardService.ChangeConversationsProject(
          applyRequest as any,
        )) as any;
        handleConversationsUpdated(response);
      }
      else
      {
        const ctx = dashboardContext as DashboardGroup[];

        // Filter invalid conversations from request
        applyRequest.ConversationsIds = validConversations.join();
        applyRequest.DuplicateConversationDetails = (
          applyRequest.DuplicateConversationDetails ?? []
        ).filter(c => validConversations.includes(c.ConversationId));

        // Include dashboard ID in request
        applyRequest.DashboardId = ctx[0]?.DashboardId ?? 0;

        const response = (await dashboardService.DuplicateDashboardProject(
          applyRequest as any,
        )) as any;
        handleDashboardDuplicated(response);
      }
    }
    catch (error)
    {
      console.error('Error applying changes:', error);
    }
  }, [
    request,
    dashboardContext,
    validConversations,
    handleConversationsUpdated,
    handleDashboardDuplicated,
  ]);

  const isDark = document.documentElement.classList.contains('dark');
  const configValueName = request.configValueName;
  const successItems = summary.filter(item => item.Status === true);
  const errorItems = summary.filter(item => item.Status === false);
  const textColor = isDark ? '#a9a093' : '#414042';
  const subtleColor = isDark ? '#6e6860' : '#a5a7ac';

  return (
    <div>
      <div
        className="changeCards"
        style={{
          height: '300px',
          display: 'block',
          background: isDark ? '#333535' : 'rgb(255,255,255)',
          padding: '24px',
          overflow: 'auto',
        }}
      >
        <span>
          <b>Summary:</b>
        </span>

        {/* Success items */}
        <div>
          {successItems.map((item, i) => (
            <div key={i}>
              <div style={{ fontSize: '12px', color: textColor, margin: '0.5em 0' }}>
                <div>
                  <div
                    style={{
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      paddingRight: '4px',
                    }}
                  >
                    <div className={item.ConfigValueIcon}></div>
                  </div>
                  <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                    <div className="detail-update-description">{item.Title}</div>
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '10px', color: subtleColor }}>{configValueName}</div>
              <div
                style={{
                  display: 'inline-block',
                  verticalAlign: 'top',
                  paddingRight: '12px',
                  paddingBottom: '8px',
                }}
              >
                <del
                  style={{
                    fontSize: '12px',
                    textDecoration: 'line-through',
                    color: isDark ? '#4a4c4c' : '#d0d0d0',
                    marginRight: '4px',
                    display: 'inline-block',
                  }}
                >
                  {!item.PrevProjectName ? 'Not Set' : item.PrevProjectName}
                </del>
                <div
                  style={{
                    color: textColor,
                    fontSize: '12px',
                    wordBreak: 'break-all',
                    display: 'inline-block',
                  }}
                >
                  {!item.NewProjectName ? 'Not Set' : item.NewProjectName}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Error items */}
        <div>
          {errorItems.map((item, i) => (
            <div key={i}>
              <div style={{ fontSize: '12px', color: textColor, margin: '0.5em 0' }}>
                <div>
                  <div
                    style={{
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      paddingRight: '4px',
                    }}
                  >
                    <div className={item.ConfigValueIcon}></div>
                  </div>
                  <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                    <div className="detail-update-description">{item.Title}</div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'inline-block', verticalAlign: 'top', paddingRight: '12px' }}>
                <div
                  style={{
                    color: textColor,
                    fontSize: '12px',
                    wordBreak: 'break-all',
                    display: 'inline-block',
                  }}
                >
                  {item.ErrorMessage}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer buttons — mirrors exact conditional from original */}
      {canChange ? (
        <div className="react-corgee-wizard_modal-footer flex justify-end gap-3 mt-5">
          <button
            className="react-corgee-wizard_cancel-btn btn btn-default"
            onClick={onCancelSummarySelected}
          >
            Cancel
          </button>
          <button
            className="react-corgee-wizard_corgee-btn btn btn-default"
            onClick={handleApplySelected}
          >
            Apply
          </button>
        </div>
      ) : (
        <div className="react-corgee-wizard_modal-footer flex justify-end gap-3 mt-5">
          <button
            className="react-corgee-wizard_cancel-btn btn btn-default"
            onClick={onCancelSummarySelected}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const ChangeCardsProject: React.FC<ChangeCardsProjectProps> = ({
  request: initialRequest,
  onConversationsUpdated,
  onClose,
  dashboardContext = {},
}) =>
{
  const [step, setStep] = useState<1 | 2>(1);
  const [request, setRequest] = useState<ProjectRequest>(() => ({
    ...initialRequest,
  }));

  const handleSelectProjectChange = useCallback((projectIdUpd: number) =>
  {
    setRequest(prev => ({
      ...prev,
      request: {
        ...prev,
        ProjectId: projectIdUpd,
      },
    }));
  }, []);

  const handleConversationsUpdated = useCallback(
    (itemsUpdated: ConversationSummary[]) =>
    {
      if (onConversationsUpdated)
      {
        onConversationsUpdated(itemsUpdated);
      }
    },
    [onConversationsUpdated],
  );

  const handlePreviewSelected = useCallback(() =>
  {
    setStep(2);
  }, []);

  // Mirrors onCancelSelected — checks dashboardContext before calling onClose
  const handleCancelSelected = (goToDuplicateMenu?: boolean, closeModal?: boolean) =>
  {
    if (onClose)
    {
      if (isDashboardContextEmpty(dashboardContext))
      {
        onClose();
      }
      else
      {
        onClose(goToDuplicateMenu, closeModal);
      }
    }
  };

  const handleCancelSummarySelected = useCallback(() =>
  {
    setStep(1);
    setRequest(prev => ({
      ...prev,
      request: {
        ...prev,
        IsPreview: 1,
      },
    }));
  }, []);

  const isDark = document.documentElement.classList.contains('dark');

  return (
    <div>
      <div
        className="changeCards ui-dialog"
        style={{ background: isDark ? '#333535' : 'rgb(255,255,255)', position: 'static' }}
      >
        {step === 1 ? (
          <ChangeCardsProjectStep1
            request={request}
            onSelectProjectChange={handleSelectProjectChange}
            onPreviewSelected={handlePreviewSelected}
            onCancelSelected={handleCancelSelected}
            dashboardContext={dashboardContext}
          />
        ) : (
          <ChangeCardsProjectStep2
            request={request}
            onConversationsUpdated={handleConversationsUpdated}
            onCancelSummarySelected={handleCancelSummarySelected}
            dashboardContext={dashboardContext}
          />
        )}
      </div>
    </div>
  );
};

export default ChangeCardsProject;
